import React from 'react';
import { BarChart, Trash2, Layers, Car, Home } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов налога
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                {calc.taxObjectType === 'car' ? (
                  <Car className="w-4 h-4 mr-1.5 text-blue-500" />
                ) : (
                  <Home className="w-4 h-4 mr-1.5 text-green-500" />
                )}
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {calc.taxObjectType === 'car' ? (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Автомобиль:</span>
                    <span className="font-medium">
                      {calc.results.carPower} л.с.
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Стоимость:</span>
                    <span>
                      {formatCurrency(calc.results.carCost, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Коэффициент:</span>
                    <span>
                      {formatNumber(calc.results.luxuryCoefficient, 2)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Объект:</span>
                    <span className="font-medium">
                      {calc.results.propertyType === 'apartment' ? 'Квартира' : 
                       calc.results.propertyType === 'house' ? 'Дом' : 
                       calc.results.propertyType === 'land' ? 'Земля' : 'Коммерч.'}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Кадастр. стоимость:</span>
                    <span>
                      {formatCurrency(calc.results.cadastralValue, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Ставка:</span>
                    <span>
                      {formatNumber(calc.results.taxRate, 2)}%
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span>Налог:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calc.results.totalTax, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedCalculations.length >= 2 && (
        <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2">
            Сравнение налоговой нагрузки
          </h4>
          <div className="space-y-2">
            {savedCalculations.map((calc) => (
              <div key={calc.id} className="flex items-center">
                <div className="w-24 text-xs text-indigo-600 dark:text-indigo-400 truncate">{calc.name.substring(0, 12)}{calc.name.length > 12 ? '...' : ''}</div>
                <div className="flex-grow ml-2">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 dark:bg-indigo-400 rounded-full"
                      style={{ 
                        width: `${Math.min(100, (calc.results.totalTax / 100000) * 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <div className="ml-2 text-xs font-medium text-indigo-700 dark:text-indigo-300 w-16 text-right">
                  {formatCurrency(calc.results.totalTax, 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;